import { gql } from '@apollo/client/core';
import {
  TRANSCRIPT_TYPE_FIELDS,
  TRANSCRIPT_REQUEST_FIELDS,
  TRANSCRIPT_ACTIVITY_FIELDS,
  GENERATED_TRANSCRIPT_FIELDS,
  TRANSCRIPT_TEMPLATE_FIELDS,
} from './fragments';

// Transcript Types — Admin CRUD

export const CREATE_TRANSCRIPT_TYPE = gql`
  mutation CreateTranscriptType($input: CreateTranscriptTypeInput!) {
    createTranscriptType(input: $input) {
      ...TranscriptTypeFields
    }
  }
  ${TRANSCRIPT_TYPE_FIELDS}
`;

export const UPDATE_TRANSCRIPT_TYPE = gql`
  mutation UpdateTranscriptType($input: UpdateTranscriptTypeInput!) {
    updateTranscriptType(input: $input) {
      ...TranscriptTypeFields
    }
  }
  ${TRANSCRIPT_TYPE_FIELDS}
`;

export const DELETE_TRANSCRIPT_TYPE = gql`
  mutation DeleteTranscriptType($id: ID!) {
    deleteTranscriptType(id: $id)
  }
`;

// Request workflow — Admin

export const APPROVE_TRANSCRIPT_REQUEST = gql`
  mutation ApproveTranscriptRequest($id: ID!) {
    approveTranscriptRequest(id: $id) {
      ...TranscriptRequestFields
    }
  }
  ${TRANSCRIPT_REQUEST_FIELDS}
`;

export const REJECT_TRANSCRIPT_REQUEST = gql`
  mutation RejectTranscriptRequest($id: ID!, $reason: String!) {
    rejectTranscriptRequest(id: $id, reason: $reason) {
      ...TranscriptRequestFields
    }
  }
  ${TRANSCRIPT_REQUEST_FIELDS}
`;

export const REGENERATE_TRANSCRIPT_DATA = gql`
  mutation RegenerateTranscriptData($transcript_request_id: ID!) {
    regenerateTranscriptData(transcript_request_id: $transcript_request_id) {
      ...GeneratedTranscriptFields
    }
  }
  ${GENERATED_TRANSCRIPT_FIELDS}
`;

export const ADMIN_GENERATE_TRANSCRIPT = gql`
  mutation AdminGenerateTranscript($input: AdminGenerateTranscriptInput!) {
    adminGenerateTranscript(input: $input) {
      ...GeneratedTranscriptFields
    }
  }
  ${GENERATED_TRANSCRIPT_FIELDS}
`;

export const ADD_TRANSCRIPT_REQUEST_NOTE = gql`
  mutation AddTranscriptRequestNote(
    $transcript_request_id: ID!
    $content: String!
    $is_visible_to_applicant: Boolean
  ) {
    addTranscriptRequestNote(
      transcript_request_id: $transcript_request_id
      content: $content
      is_visible_to_applicant: $is_visible_to_applicant
    ) {
      ...TranscriptActivityFields
    }
  }
  ${TRANSCRIPT_ACTIVITY_FIELDS}
`;

export const LINK_TRANSCRIPT_APPLICANT_TO_STUDENT = gql`
  mutation LinkTranscriptApplicantToStudent($transcript_request_id: ID!, $student_id: ID!) {
    linkTranscriptApplicantToStudent(
      transcript_request_id: $transcript_request_id
      student_id: $student_id
    ) {
      ...TranscriptRequestFields
    }
  }
  ${TRANSCRIPT_REQUEST_FIELDS}
`;

export const MARK_TRANSCRIPT_DISPATCHED = gql`
  mutation MarkTranscriptDispatched($transcript_request_id: ID!) {
    markTranscriptDispatched(transcript_request_id: $transcript_request_id) {
      ...TranscriptRequestFields
    }
  }
  ${TRANSCRIPT_REQUEST_FIELDS}
`;

export const UPDATE_TRANSCRIPT_COVER_LETTER = gql`
  mutation UpdateTranscriptCoverLetter($generated_transcript_id: ID!, $cover_letter: String!) {
    updateTranscriptCoverLetter(
      generated_transcript_id: $generated_transcript_id
      cover_letter: $cover_letter
    ) {
      ...GeneratedTranscriptFields
    }
  }
  ${GENERATED_TRANSCRIPT_FIELDS}
`;

// Transcript Templates — Admin CRUD

export const CREATE_TRANSCRIPT_TEMPLATE = gql`
  mutation CreateTranscriptTemplate($input: CreateTranscriptTemplateInput!) {
    createTranscriptTemplate(input: $input) {
      ...TranscriptTemplateFields
    }
  }
  ${TRANSCRIPT_TEMPLATE_FIELDS}
`;

export const UPDATE_TRANSCRIPT_TEMPLATE = gql`
  mutation UpdateTranscriptTemplate($input: UpdateTranscriptTemplateInput!) {
    updateTranscriptTemplate(input: $input) {
      ...TranscriptTemplateFields
    }
  }
  ${TRANSCRIPT_TEMPLATE_FIELDS}
`;

export const DELETE_TRANSCRIPT_TEMPLATE = gql`
  mutation DeleteTranscriptTemplate($id: ID!) {
    deleteTranscriptTemplate(id: $id)
  }
`;

export const SET_PROGRAMME_TRANSCRIPT_TEMPLATE_DEFAULT = gql`
  mutation SetProgrammeTranscriptTemplateDefault($programme_id: ID!, $transcript_template_id: ID) {
    setProgrammeTranscriptTemplateDefault(
      programme_id: $programme_id
      transcript_template_id: $transcript_template_id
    ) {
      id
      program_name
    }
  }
`;

// PDF Transcript Config — School-level settings

export const UPDATE_PDF_TRANSCRIPT_CONFIG = gql`
  mutation UpdatePdfTranscriptConfig($input: PdfTranscriptConfigInput!) {
    updatePdfTranscriptConfig(input: $input) {
      preset
      logo_position
      logo_height
      show_school_name
      show_school_address
      show_report_title
      report_title
      report_subtitle
      font_family
      font_size
      header_font_size
      table_header_font_size
      row_padding
      show_borders
      striped_rows
      alternate_row_color
      show_student_info
      student_info_fields
      student_info_layout
      show_cover_letter
      show_cumulative
      show_semester_summary
      show_partial_footnote
      show_footer
      footer_text
      show_estamp
      show_signature_lines
      signature_labels
      page_size
      page_orientation
      page_margin
      show_watermark
      watermark_text
    }
  }
`;

// Student-facing

export const SUBMIT_TRANSCRIPT_REQUEST = gql`
  mutation SubmitTranscriptRequest($input: SubmitTranscriptRequestInput!) {
    submitTranscriptRequest(input: $input) {
      ...TranscriptRequestFields
    }
  }
  ${TRANSCRIPT_REQUEST_FIELDS}
`;

export const CONFIRM_TRANSCRIPT_PAYMENT = gql`
  mutation ConfirmTranscriptPayment($transcript_request_id: ID!, $payment_reference: String!) {
    confirmTranscriptPayment(
      transcript_request_id: $transcript_request_id
      payment_reference: $payment_reference
    ) {
      ...TranscriptRequestFields
    }
  }
  ${TRANSCRIPT_REQUEST_FIELDS}
`;

// Student reply to transcript request (authenticated)

export const REPLY_TO_TRANSCRIPT_REQUEST = gql`
  mutation ReplyToTranscriptRequest(
    $transcript_request_id: ID!
    $content: String!
    $attachment: Upload
  ) {
    replyToTranscriptRequest(
      transcript_request_id: $transcript_request_id
      content: $content
      attachment: $attachment
    ) {
      ...TranscriptActivityFields
    }
  }
  ${TRANSCRIPT_ACTIVITY_FIELDS}
`;

// Public reply to transcript request (unauthenticated, via tracking code)

export const REPLY_TO_TRANSCRIPT_REQUEST_PUBLIC = gql`
  mutation ReplyToTranscriptRequestPublic(
    $tracking_code: String!
    $applicant_email: String!
    $content: String!
    $attachment: Upload
  ) {
    replyToTranscriptRequestPublic(
      tracking_code: $tracking_code
      applicant_email: $applicant_email
      content: $content
      attachment: $attachment
    ) {
      ...TranscriptActivityFields
    }
  }
  ${TRANSCRIPT_ACTIVITY_FIELDS}
`;

// Public — no auth required

export const SUBMIT_PUBLIC_TRANSCRIPT_REQUEST = gql`
  mutation SubmitPublicTranscriptRequest(
    $school_slug: String!
    $transcript_type_id: ID!
    $applicant_name: String!
    $applicant_email: String!
    $applicant_phone: String
    $matric_no: String
    $student_status: StudentAcademicStatusEnum!
    $supporting_document: Upload
    $applicant_category: ApplicantCategoryEnum!
    $programme_id: ID
    $programme_name: String
    $year_of_admission: String
    $year_of_graduation: String
    $destination_address: String
    $destination_institution: String
    $recipient_email: String
    $courier_name: String
    $courier_address: String
    $courier_phone: String
    $number_of_copies: Int
    $complaint_note: String
  ) {
    submitPublicTranscriptRequest(
      school_slug: $school_slug
      transcript_type_id: $transcript_type_id
      applicant_name: $applicant_name
      applicant_email: $applicant_email
      applicant_phone: $applicant_phone
      matric_no: $matric_no
      student_status: $student_status
      supporting_document: $supporting_document
      applicant_category: $applicant_category
      programme_id: $programme_id
      programme_name: $programme_name
      year_of_admission: $year_of_admission
      year_of_graduation: $year_of_graduation
      destination_address: $destination_address
      destination_institution: $destination_institution
      recipient_email: $recipient_email
      courier_name: $courier_name
      courier_address: $courier_address
      courier_phone: $courier_phone
      number_of_copies: $number_of_copies
      complaint_note: $complaint_note
    ) {
      id
      tracking_code
      status
      applicant_name
      applicant_email
      bill {
        id
        amount
        net_amount
        status
      }
    }
  }
`;

export const RESOLVE_MATRIC_RETRIEVAL_ATTEMPT = gql`
  mutation ResolveMatricRetrievalAttempt($id: ID!, $student_id: ID!, $admin_notes: String) {
    resolveMatricRetrievalAttempt(id: $id, student_id: $student_id, admin_notes: $admin_notes) {
      id
      status
      matchedStudent {
        id
        matric_no
        first_name
        surname
      }
      resolver {
        id
        name
      }
      resolved_at
      admin_notes
    }
  }
`;

export const DISMISS_MATRIC_RETRIEVAL_ATTEMPT = gql`
  mutation DismissMatricRetrievalAttempt($id: ID!, $admin_notes: String) {
    dismissMatricRetrievalAttempt(id: $id, admin_notes: $admin_notes) {
      id
      status
      resolver {
        id
        name
      }
      resolved_at
      admin_notes
    }
  }
`;
